import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Mutation } from '@apollo/client/react/components';
import { Grid, Cell } from 'layout-core-components';
import { Spinner } from 'spinner';
import { colors } from 'bench-style-constants';

import { LockIcon } from './Icons';
import InternalNote from './InternalNote';
import {
  LedgerInternalNotesMutation,
  LedgerPostInternalNoteMutation,
  TransactionInternalNotesMutation,
  TransactionPostInternalNoteMutation,
} from './queries';
import MessageInputBox from './MessageInputBox';
import { InternalRefetchQueryDescriptor } from '@apollo/client';
import { InternalNote as InternalNoteType } from './types';

const Card = styled.div`
  padding: 12px;
  background: ${colors.white};
`;

const Title = styled.div`
  color: ${colors.cloud};
  font-size: 14px;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px;
`;

const MessageInputBoxWrapper = styled.div`
  margin-top: 12px;
`;

const addIdsToInternalNotes = internalNotes => {
  let id = 0;
  return internalNotes.map(note => {
    id += 1;
    return { id, ...note };
  });
};

type InternalNotesContainerProps = {
  internalNotes: InternalNoteType[];
  ledgerId?: string;
  journalEntryId?: string;
  refetchQueries?: InternalRefetchQueryDescriptor[];
};

class InternalNotesContainer extends Component<InternalNotesContainerProps> {
  static defaultProps = {
    ledgerId: undefined,
    journalEntryId: undefined,
    refetchQueries: [],
  };

  onDeleteNote = (
    id: number,
    submitInternalNote: (note: Record<string, unknown>) => Promise<any>,
  ) => {
    const { ledgerId, journalEntryId, internalNotes } = this.props;

    const internalNotesWithIds = addIdsToInternalNotes(internalNotes);

    submitInternalNote({
      variables: {
        internalNotes: internalNotesWithIds.filter(note => note.id !== id),
        ledgerId,
        journalEntryId,
      },
    });
  };

  onEditNote = (
    id: number,
    message: string,
    submitInternalNote: (note: Record<string, unknown>) => Promise<any>,
  ) => {
    this.onUpdateNote(id, message, false, submitInternalNote);
  };

  onFlagNote = (
    id: number,
    submitInternalNote: (note: Record<string, unknown>) => Promise<any>,
  ) => {
    this.onUpdateNote(id, '', true, submitInternalNote);
  };

  onUpdateNote = (
    id: number,
    message: string,
    toggleFlag: boolean,
    submitInternalNote: (note: Record<string, unknown>) => Promise<any>,
  ) => {
    const { ledgerId, journalEntryId, internalNotes } = this.props;

    const internalNotesWithIds = addIdsToInternalNotes(internalNotes);
    const editedInternalNotes = internalNotesWithIds.map(note => {
      if (note.id === id) {
        return {
          ...note,
          body: message || note.body,
          flagged: toggleFlag ? !note.flagged : note.flagged,
          timestamp: moment().unix(),
        };
      }

      return note;
    });

    submitInternalNote({
      variables: {
        internalNotes: editedInternalNotes,
        ledgerId,
        journalEntryId,
      },
    });
  };

  onPostNote = (
    note: string,
    postNewInternalNote: (note: Record<string, unknown>) => Promise<any>,
  ) => {
    const { ledgerId, journalEntryId } = this.props;

    postNewInternalNote({
      variables: {
        noteText: note,
        ledgerId,
        journalEntryId,
      },
    });
  };

  render() {
    const { ledgerId, refetchQueries, internalNotes } = this.props;

    const internalNotesWithIds = addIdsToInternalNotes(internalNotes);

    const submitInternalNotesMutation = ledgerId
      ? LedgerInternalNotesMutation
      : TransactionInternalNotesMutation;

    const postInternalNotesMutation = ledgerId
      ? LedgerPostInternalNoteMutation
      : TransactionPostInternalNoteMutation;

    return (
      <Card>
        <Title>
          <LockIcon /> Internal Notes
        </Title>
        <Mutation mutation={submitInternalNotesMutation} refetchQueries={refetchQueries}>
          {(submitInternalNote, { loading, error }) => {
            if (error) {
              return <>Error! ${error.message}</>;
            }

            return (
              <Fragment>
                {loading && (
                  <SpinnerWrap>
                    <Spinner />
                  </SpinnerWrap>
                )}
                {!loading && internalNotesWithIds.length > 0 && (
                  <Grid column rowSpacing="medium">
                    {internalNotesWithIds.map(note => (
                      <Cell size="auto" key={note.id}>
                        <InternalNote
                          id={note.id}
                          username={`${note.userFirstName} ${note.userLastName}`}
                          body={note.body}
                          timestamp={note.timestamp}
                          isFlagged={note.flagged}
                          onDeleteNote={() => this.onDeleteNote(note.id, submitInternalNote)}
                          onEditNote={msg => this.onEditNote(note.id, msg, submitInternalNote)}
                          onFlagNote={() => this.onFlagNote(note.id, submitInternalNote)}
                        />
                      </Cell>
                    ))}
                  </Grid>
                )}
              </Fragment>
            );
          }}
        </Mutation>
        <Mutation mutation={postInternalNotesMutation} refetchQueries={refetchQueries}>
          {(postNewInternalNote, { loading, error }) => {
            if (error) {
              return <>Error! ${error.message}</>;
            }

            return (
              <Fragment>
                {loading && (
                  <SpinnerWrap>
                    <Spinner />
                  </SpinnerWrap>
                )}
                {!loading && (
                  <MessageInputBoxWrapper>
                    <MessageInputBox
                      placeholder="Leave a note for your team"
                      submitButtonText="Post"
                      onPost={note => this.onPostNote(note, postNewInternalNote)}
                    />
                  </MessageInputBoxWrapper>
                )}
              </Fragment>
            );
          }}
        </Mutation>
      </Card>
    );
  }
}

export default InternalNotesContainer;
